// ──────────────────────────────────────────────────────
// BudgetCard — compact value-oriented card (role='budget').
// Layout: header row (label chip on the start / savings pill on the end,
// both in regular flow) above the existing 80x80 image + text horizontal
// row. No absolute positioning — the savings pill no longer overlaps the
// image. Renders budget-specific reasonsHeader plus up to 2 bullet reasons.
// ──────────────────────────────────────────────────────
import Link from 'next/link';
import type { ProductSuggestion } from '@/lib/types';
import { TrustBadge } from '@/components/TrustBadge';
import { isProductOutOfStock, OUT_OF_STOCK_LABEL_AR } from './availability-badge';

export function BudgetCard({ product }: { product: ProductSuggestion }) {
    const ps = product;
    const bullets = (ps.reasons && ps.reasons.length > 0)
        ? ps.reasons.slice(0, 2)
        : (ps.shortReasonAr ? [ps.shortReasonAr] : []);
    const hasSavings = typeof ps.savingsPercent === 'number' && ps.savingsPercent > 0;
    const outOfStock = isProductOutOfStock(ps);

    // QCARE-AI-DEV-FRONT-DARK-CARDS (2026-05-09): branch the accent on
    // labelKind. R6J's fallback-budget path (alt is NOT meaningfully
    // cheaper than hero) sets labelKind='alternative'; in that case the
    // card switches from the gold/amber "value" accent to a teal/neutral
    // "alternative" accent. Backend also already swaps the label text
    // ('بديل مناسب 🔄' vs 'أفضل قيمة 💰'); this just keeps the visual
    // accent in lockstep so a reader doesn't see "بديل مناسب" wearing
    // gold value-styling.
    const isAlternative = ps.labelKind === 'alternative' || ps.icon === '🔄';
    const accent = isAlternative ? '#7ec2ce' : '#fbbf24';     // teal vs amber
    const accentSoft = isAlternative ? 'rgba(126,194,206,0.12)' : 'rgba(251,191,36,0.14)';
    const accentBorder = isAlternative ? 'rgba(126,194,206,0.32)' : 'rgba(251,191,36,0.40)';

    return (
        <Link
            href={ps.href}
            className="block rounded-xl overflow-hidden transition-shadow hover:shadow-lg cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
            style={{
                background: 'linear-gradient(135deg, #0a1f24 0%, #0f2a30 50%, #0a1f24 100%)',
                border: `1px solid ${accentBorder}`,
                textDecoration: 'none',
                direction: 'rtl',
                boxShadow: '0 6px 18px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.04)',
            }}
        >
            {/* Header row: label pill (start) + out-of-stock + savings pill (end).
                Trust badge moves to the bottom row for visual parity with HeroCard. */}
            <div className="flex justify-between items-center gap-2 px-3 pt-2.5 pb-1">
                {ps.label ? (
                    <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold"
                        style={{
                            background: accentSoft,
                            color: accent,
                            border: `1px solid ${accentBorder}`,
                        }}
                    >
                        {ps.icon && <span aria-hidden>{ps.icon}</span>}
                        <span>{ps.label}</span>
                    </span>
                ) : (
                    <span />
                )}
                <div className="flex items-center gap-1.5">
                    {outOfStock && (
                        <span
                            className="inline-flex items-center gap-1 rounded-md text-[10px] font-semibold"
                            style={{
                                background: 'rgba(180,35,24,0.15)',
                                color: '#fca5a5',
                                border: '1px solid rgba(252,165,165,0.40)',
                                padding: '2px 6px',
                            }}
                            aria-label={OUT_OF_STOCK_LABEL_AR}
                        >
                            {OUT_OF_STOCK_LABEL_AR}
                        </span>
                    )}
                    {hasSavings && (
                        <span
                            className="px-2 py-0.5 rounded-full text-[10px] font-bold"
                            style={{ background: '#10b981', color: '#ffffff' }}
                        >
                            وفر {ps.savingsPercent}٪
                        </span>
                    )}
                </div>
            </div>

            {/* Content row: image panel (cream) + text block (dark) */}
            <div className="flex">
                <div
                    className="flex-shrink-0 flex items-center justify-center"
                    style={{ width: 80, height: 80, background: '#eaf3f5' }}
                >
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                        src={ps.imageUrl}
                        alt={ps.nameAr}
                        className="w-full h-full object-contain p-1.5 q-card-image-fade"
                        onLoad={(e) => {
                            const el = e.target as HTMLImageElement;
                            if (el.dataset.fallback === '1') return;
                            el.style.opacity = '1';
                        }}
                        onError={(e) => {
                            const el = e.target as HTMLImageElement;
                            if (el.dataset.fallback === '1') return;
                            el.dataset.fallback = '1';
                            el.src = '/branding/qio-logo.jpeg';
                            el.style.opacity = '0.55';
                        }}
                    />
                </div>

                <div
                    className="flex-1 p-2.5 flex flex-col justify-center min-w-0"
                    style={{ borderRight: '1px solid rgba(126,194,206,0.18)' }}
                >
                    <div
                        className="text-sm font-semibold leading-tight mb-0.5"
                        style={{
                            color: '#7ec2ce',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                        }}
                    >
                        {ps.nameAr}
                    </div>

                    {ps.brandAr && (
                        <div className="text-[10px] mb-0.5" style={{ color: 'rgba(255,255,255,0.55)' }}>
                            {ps.brandAr}
                        </div>
                    )}

                    {ps.price != null && ps.price > 0 && (
                        <div
                            className="text-base font-bold mb-0.5"
                            style={{
                                color: '#a8dce4',
                                textShadow: '0 1px 6px rgba(126,194,206,0.20)',
                            }}
                        >
                            {ps.price.toFixed(2)} ريال
                        </div>
                    )}

                    {bullets.length > 0 && (
                        <div>
                            {ps.reasonsHeader && (
                                <div className="text-[10px] font-bold mb-0.5" style={{ color: accent }}>
                                    {ps.reasonsHeader}
                                </div>
                            )}
                            <ul className="space-y-0.5 m-0 p-0 list-none">
                                {bullets.map((b, i) => (
                                    <li
                                        key={i}
                                        className="text-[10px] leading-snug flex gap-1"
                                        style={{ color: 'rgba(255,255,255,0.78)' }}
                                    >
                                        <span aria-hidden style={{ color: accent, flexShrink: 0 }}>•</span>
                                        <span
                                            style={{
                                                display: '-webkit-box',
                                                WebkitLineClamp: 1,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            {b}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>

            {/* Bottom trust pill — single compact authenticity bar so the
                header stays clean. onClick swallows navigation so tapping
                the pill opens the certificate modal (TrustBadge handles
                its own modal trigger) instead of routing to the product. */}
            <div
                className="px-3 pb-2 pt-1 flex justify-end"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
            >
                <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold"
                    style={{
                        background: 'rgba(126,194,206,0.10)',
                        border: '1px solid rgba(126,194,206,0.28)',
                        color: '#a8dce4',
                    }}
                >
                    <span aria-hidden>🔒</span>
                    <span>أصلي 100%</span>
                    <span aria-hidden style={{ opacity: 0.50 }}>·</span>
                    <TrustBadge compact />
                </span>
            </div>
        </Link>
    );
}
